import { Injectable } from '@angular/core';
import { LocalStorageService } from './localStorage.service';
import { PrintService } from './print.service';
import { RN_DOWNLOAD_CUSTOM_REPORT } from '../interfaces/rn-data-transfer.interface';
import { RN_HTTP_METHOD } from '../enums/react-device.enum';

// Declare the external function for sending data to React Native
declare function send_data(params: string): any;

@Injectable({
  providedIn: 'root',
})
export class RNDataTransferService {
  constructor(
    private _localStorage: LocalStorageService,
    private _printService: PrintService
  ) {}

  isReactNativeApp(): boolean {
    return this._localStorage.get('is_react') === '1';
  }

  sendDownloadCustomReportActionToRN(data: RN_DOWNLOAD_CUSTOM_REPORT): void {
    // Sends data to React Native for downloading various resources within the app.

    // Check if the method property exists in the data object
    const httpMethod = data.method
      ? data.method
      : RN_HTTP_METHOD.DEFAULT_METHOD;
    const downloadAction = 'download_custom_report';

    const downloadActionData = {
      ...data,
      action: downloadAction,
      method: httpMethod,
    };

    const rnData = this.preparePayloadForRN(downloadActionData);
    send_data(rnData);
  }

  sendS3UrlToRNForDownload(s3Url: string): void {
    // Sends data to React Native for downloading s3 file url.
    const currentVersion = this._printService.appVersion;
    const reportingServiceSupportVersion = '2.5.7';
    const isReportingServiceSupported =
      String(currentVersion) >= reportingServiceSupportVersion;
    const downloadAction = isReportingServiceSupported
      ? 'download_s3_url'
      : 'download_inventory_report';
    const downloadActionData = {
      url: s3Url,
      action: downloadAction,
    };

    const rnData = JSON.stringify(downloadActionData);
    send_data(rnData);
  }

  private preparePayloadForRN(data: any): string | null {
    // Prepares data for transmission to React Native by adding authorization token
    // returns prepared payload string or null if error occurs.

    try {
      const token = this._localStorage.get('token');
      if (token) {
        // Add the authorization token to the payload
        const payload = {
          ...data,
          authorizationToken: `Bearer ${token}`,
        };
        return JSON.stringify(payload);
      }
      // Return the original stringified data if the token doesn't exist
      return JSON.stringify(data);
    } catch (error) {
      console.error(`Error preparing payload for React Native: ${error}`);
      return null;
    }
  }

  sendRNToOpenLink(redirectUrl: string): void {
    const rnData = JSON.stringify({
      "action": "OPEN_DELIVERY_URL",
      "redirectUrl": redirectUrl,
    });
    send_data(rnData);
  }

  sendRNForOpenUrlInBrowser(url: string): void {
    const rnData = JSON.stringify({
      "action": "OPEN_IN_BROWSER",
      "url": url
    });
    send_data(rnData);
  }

  sendRNForRedirectToStore(androidUrl: string, iosUrl: string): void {
    const rnData = JSON.stringify({
      "action": "REDIRECT_TO_STORE",
      "androidUrl": androidUrl,
      "iosUrl": iosUrl
    });
    send_data(rnData);
  }
}
